// src/hooks/useCompanyProfile.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CompanyService, CompanyProfile } from '../admin/pages/company/companyService';

// ─────────────────────────────────────────────────────────────────────────────
// QUERY KEYS
//
// Centralised key factory so every part of the app uses identical keys.
// React Query matches keys by deep equality, so structure matters.
//
//   COMPANY_KEYS.all      → invalidates EVERYTHING under 'company'
//   COMPANY_KEYS.profile  → the single company profile document
//
// Unlike brands/categories there is no pagination, no list variants, and no
// stats query — this collection holds exactly one document, so a single key
// family is enough.
// ─────────────────────────────────────────────────────────────────────────────
export const COMPANY_KEYS = {
  all:     ['company'] as const,
  profile: () => ['company', 'profile'] as const,
};

// ─────────────────────────────────────────────────────────────────────────────
// useCompanyProfile
//
// Fetches the single company profile document from Firestore.
// Cached under COMPANY_KEYS.profile() with a 5-minute stale window —
// company details rarely change during a session, so a longer window is fine.
//
// `enabled: !!userId` prevents the query from firing before the auth context
// has resolved and a valid user is confirmed.
// ─────────────────────────────────────────────────────────────────────────────
export const useCompanyProfileQuery = (userId: string | undefined) => {
  return useQuery({
    queryKey: COMPANY_KEYS.profile(),
    queryFn: () => CompanyService.getCompanyProfile(),
    staleTime: 1000 * 60 * 5,   // 5 minutes — profile data is slow to change
    enabled: !!userId,           // only run when a user is authenticated
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// useCompanyMutations
//
// Returns two mutations: create and update.
//
// After every successful mutation the profile cache is invalidated.
// React Query will automatically refetch the profile the next time the
// component renders, keeping the UI in sync without a manual refresh call.
//
// We invalidate COMPANY_KEYS.all which covers the profile() key in one call —
// any query whose key starts with ['company'] is invalidated.
// ─────────────────────────────────────────────────────────────────────────────
export const useCompanyMutations = () => {
  const queryClient = useQueryClient();

  // Invalidate all company queries after any write
  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: COMPANY_KEYS.all });
  };

  // ── Create ────────────────────────────────────────────────────────────────
  // Called when no profile exists yet. Service validates, creates the doc,
  // and returns the new document ID.
  const create = useMutation({
    mutationFn: (profile: CompanyProfile) =>
      CompanyService.createCompanyProfile(profile),
    onSuccess: invalidateAll,
  });

  // ── Update ────────────────────────────────────────────────────────────────
  // Called when a profile already exists. Service fetches the existing doc ID
  // internally then calls updateDoc.
  const update = useMutation({
    mutationFn: (profile: CompanyProfile) =>
      CompanyService.updateCompanyProfile(profile),
    onSuccess: invalidateAll,
  });

  return { create, update };
};